import { ExpressionModelDetail, ExpressionContextEntity, ExtendFunction, FunctionEntity, FuncType, Parameter, VarEntity } from "../common/ExpressEntity";
import { BasePathService } from '@farris/rtf';
import { EsprimeAnalyer } from "./EsprimaAnalyer";
import { CheckContext, findContextVariable, getDataModelProperty, getDataModelPropertyFromFunctionParam } from "../checkSyntax/CheckExpress";
import { buildNewSchemaEntityStruct, buildSchemaEntityStruct } from "../checkSyntax/SchemaToStruct";
import { ExpressionEditorLocalePipe } from "./expression-editor-locale.pipe";

export class CommonUtils {

  //变量函数的key
  public static variableFuncKeys: string[] = ["GetContextParameter", "GetSessionValue"];

  public static esprima: any = null;

  // 初始化
  public static init() {
    if ((window as any).esprima == null) {
      let loaded = CommonUtils.loadEsprima();
      if (loaded != null) {
        loaded.onload = function () {
          // console.log("CommonUtils Loading Esprima over");
          CommonUtils.esprima = (window as any).esprima;
        }
      }
    }
  }

  /**
   * Esprima分词：获取tokens
   * @param expressionText 表达式文本
   * @returns tokens:any[]
   */
  public static getTokens(expressionText: any): any[] {
    let tokens = [];

    //加载Esprima
    if (this.esprima == null) {
      return tokens;
    } else {
      let wholeTokens = this.esprima.tokenize(expressionText);
      for (let i = 0; i < wholeTokens.length; i++) {
        tokens.push(wholeTokens[i].value);
      }

      //根据expressionText，获取需要填充的字符（esprima分词后导致空格、换行符丢失，需要手动记录并添加回来）
      let whites = [];
      for (let i = 0; i < tokens.length; i++) {
        //遍历
        let pos = expressionText.indexOf(tokens[i]);
        if (pos != -1) {
          whites.push(expressionText.substring(0, pos))
        } else {
          whites.push("");
        }
        expressionText = expressionText.slice(pos + tokens[i].length);

        //最后一位特殊处理
        if (i == tokens.length - 1) {
          whites.push(expressionText);
        }
      }

      //合并tokens和whites
      let result = [];
      let total = tokens.length;
      for (let i = 0; i < total; i++) {
        if (whites[i] != "") result.push(whites[i]);
        if (tokens[i] != "") result.push(tokens[i]);

        if (i == total - 1) {
          if (whites[i + 1] != "") result.push(whites[i + 1]);
        }
      }

      return result;
    }
  }

  public static getIdentifierAndVariable(expressions: any, context: ExpressionContextEntity, locale: ExpressionEditorLocalePipe): Promise<Map<string, ExpressionModelDetail>> {
    if (expressions == null && expressions.length == 0) return new Promise(function (resolve) { resolve(null) });

    // load esprima
    if (CommonUtils.esprima == null) {
      return new Promise(function (resolve) {
        let loaded = CommonUtils.loadEsprima();
        if (loaded != null) {
          loaded.onload = function () {
            CommonUtils.esprima = (window as any).esprima;

            let map = CommonUtils.doGetIdentifierAndVariable(expressions, context, locale);
            resolve(map);
          }
        }
      });
    } else {
      let map = CommonUtils.doGetIdentifierAndVariable(expressions, context, locale);
      return new Promise(function (resolve) { resolve(map) });
    }
  }

  private static doGetIdentifierAndVariable(expressions: any, context: ExpressionContextEntity, locale: ExpressionEditorLocalePipe) {
    var map = new Map<string, ExpressionModelDetail>();
    for (let expression of expressions) {
      let value: ExpressionModelDetail;


      // extract expr
      let expr = JSON.parse(expression).expr;
      let tokens = CommonUtils.getTokens(expr);

      if (map.has(expr)) continue;
      else value = new ExpressionModelDetail;


      // variable
      let contextMap = CommonUtils.getVariableMap(context.contextEntities, null, "Key2Name");
      let contextVariables = findContextVariable(tokens, contextMap);
      value.contextVariables = value.contextVariables.concat(contextVariables);

      let sessionMap = CommonUtils.getVariableMap(null, context.sessionEntities, "Key2Name");
      let sessionVariables = findContextVariable(tokens, sessionMap);
      value.sessionVariables = value.sessionVariables.concat(sessionVariables);


      // data model
      let proerties = [];
      let globalContext: CheckContext = new CheckContext(locale);
      for (let injectedEntity of context.injectedEntities) {
        let tempList = [];
        tempList.push(injectedEntity);
        let singleCheckContext: CheckContext = new CheckContext(locale);
        if (context.newSchemaMap.size != 0 && context.injectedEntities.length != 0)
          buildNewSchemaEntityStruct(singleCheckContext, context.newSchemaMap, tempList);
        else if (context.schemaMap.size != 0 && context.injectedEntities.length != 0)
          buildSchemaEntityStruct(singleCheckContext, context.schemaMap, tempList);

        proerties = proerties.concat(getDataModelProperty(tokens, singleCheckContext));

        proerties = proerties.concat(getDataModelPropertyFromFunctionParam(tokens, singleCheckContext));

        // add single entityStruct to global
        globalContext.entityStructs = globalContext.entityStructs.concat(singleCheckContext.entityStructs)
      }

      proerties = CommonUtils.removeDuplicateList(proerties);

      value.properties = CommonUtils.analyProperty(proerties, globalContext.entityStructs);

      value.properties = CommonUtils.removeDuplicate(value.properties, "code");
      map.set(expression, value);
    }
    console.log(map);

    return map;
  }

  private static analyProperty(proerties: any, tables: any) {
    let codeList = [];
    for (let i = 0; i < proerties.length; i++) {
      // remove "[*]" 
      let prop = proerties[i];
      if (prop.includes("[")) {
        let s = prop.indexOf("[");
        let e = prop.indexOf("]");
        codeList[i] = prop.substring(0, s) + prop.substring(e + 1, prop.length);
      } else {
        codeList[i] = prop;
      }
    }

    // allProperties remove "[*]", like: main.children.prop
    let allProperties = [];
    let mainTables = tables.filter(t => { return t.parent == null });
    for (let t of mainTables) {
      CommonUtils.cycleGetProperty(t, tables, allProperties, t.tableName);
    }

    // find name&type from allProperties for element of codeList
    let result = [];
    for (let i = 0; i < codeList.length; i++) {
      let list = allProperties.filter(x => { return x.code == codeList[i] });
      if (list.length > 0) {
        let res = list[0];
        result.push({ code: codeList[i], name: res.name, type: res.type });
      }
    }

    return result;
  }

  private static cycleGetProperty(t, tables, properties, prefix) {
    for (let prop of t.properties) {
      if (prop.type == "Type") {
        let sub = tables.filter(x => { return (x.parent == t.tableName || x.parent + "s" == t.tableName) && x.tableName == prop.code })[0];
        this.cycleGetProperty(sub, tables, properties, prefix + "." + sub.tableName);
        properties.push({ code: prefix + "." + sub.tableName, name: t.name, type: "Type" });
      } else if (prop.type == "Set") {
        let sub = tables.filter(x => { return (x.parent == t.tableName || x.parent + "s" == t.tableName) && x.tableName == prop.code })[0];
        this.cycleGetProperty(sub, tables, properties, prefix + "." + sub.tableName);
        properties.push({ code: prefix + "." + sub.tableName, name: t.name, type: "Set" });
      } else {
        properties.push({ code: prefix + "." + prop.code, name: prop.name, type: prop.type });
      }
    }
  }

  /**
   * 
   * @param text 表达式文本
   */
  public static getIdentifierFromJS(text: string) {
    //加载Esprima
    if (this.esprima == null) {
      return null;
    } else {
      let syntax = null;
      let identifier = [];
      try {
        syntax = this.esprima.parse(text);
      } catch (e) {
        let newText = "function test() { TEXT }";
        newText = newText.replace("TEXT", text);

        syntax = this.esprima.parse(newText);

        if (syntax.body != null && syntax.body.length == 1 && syntax.body[0].type == "FunctionDeclaration" && syntax.body[0].id.name == "test") {
          syntax = syntax.body[0].body;
        }
      }
      console.log(syntax);
      if (syntax != null && syntax.body != null) {
        for (let i = 0; i < syntax.body.length; i++) {
          let ele = syntax.body[i];
          this.analyExpressionStatement(ele, identifier);
        }
      }

      //过滤函数和扩展
      identifier = identifier.filter(x => { return !x.symbol.startsWith("DefaultFunction.") && !x.symbol.startsWith("DefaultExtendFunctionProxy.") });
      console.log("全部变量");
      console.log(identifier);

      return identifier;
    }
  }

  private static analyExpressionStatement(ele: any, list: any[]) {
    // 每个语法元素
    if (ele.type == "ExpressionStatement") {
      this.analyExpressionStatement(ele.expression, list);
    } else if (ele.type == "IfStatement") {
      let ids = EsprimeAnalyer.analyIfStatement(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "MemberExpression") {
      let res = EsprimeAnalyer.analyMemberExpression(ele);
      list.push({ symbol: res, type: "Identifier" });
    } else if (ele.type == "VariableDeclaration") {
      let ids = EsprimeAnalyer.analyVariableDeclaration(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "ReturnStatement") {
      let ids = EsprimeAnalyer.analyReturnStatement(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "AssignmentExpression") {
      let ids = EsprimeAnalyer.analyAssignmentExpression(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "Identifier") {
      let ids = EsprimeAnalyer.analyIdentifier(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "Literal") {
      let ids = EsprimeAnalyer.analyLiteral(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "ConditionalExpression") {
      let ids = EsprimeAnalyer.analyConditionalExpression(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "BinaryExpression") {
      let ids = EsprimeAnalyer.analyBinaryExpression(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "CallExpression") {
      let ids = EsprimeAnalyer.analyCallExpression(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "UnaryExpression") {
      let ids = EsprimeAnalyer.analyUnaryExpression(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "LogicalExpression") {
      let ids = EsprimeAnalyer.analyLogicalExpression(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    } else if (ele.type == "ForStatement") {
      let ids = EsprimeAnalyer.analyForStatement(ele);
      ids = this.filterAndRemoveDuplicate(ids);
      ids.forEach(x => list.push(x));
    }
  }

  private static filterAndRemoveDuplicate(list: any) {
    list = this.filterNotLiteral(list)
    return this.removeDuplicate(list, "symbol");
  }

  private static filterNotLiteral(list: any) {
    let values = [];
    for (let ele of list) {
      if (ele.type != "Literal") {
        values.push(ele);
      }
    }

    return values;
  }

  /**
   * 根据对象属性去重
   */
  private static removeDuplicate(list: any, pro: string) {
    let values = [];
    for (let ele of list) {
      if (values.findIndex(x => x[pro] == ele[pro]) < 0) {
        values.push(ele);
      }
    }

    return values;
  }

  private static removeDuplicateList(list: any) {
    let values = [];
    for (let ele of list) {
      if (!values.includes(ele)) {
        values.push(ele);
      }
    }

    return values;
  }

  /**
   * 将变量转化为映射字典
   * 解决：变量名称重复问题
   * @param contexts 上下文变量
   * @param sessions 会话变量
   * @param type 映射表类型 Key2Name || Name2Key
   * @returns 变量key和name的map映射表
   */
  public static getVariableMap(contexts: VarEntity[], sessions: VarEntity[], type: string): Map<string, string> {
    //合并变量到lists
    let lists = [];
    contexts != null && contexts.forEach(entity => { lists.push(entity); })
    sessions != null && sessions.forEach(entity => { lists.push(entity); })
    //定义返回值并开始转化
    let map = new Map;
    if (lists != null && lists.length > 0) {
      let tempLists = lists;
      while (tempLists.length > 0) {
        let filterTempLists = tempLists.filter(i => i.name == tempLists[0].name);
        if (filterTempLists.length > 1) {
          tempLists.filter(i => i.name == tempLists[0].name).forEach(varEntity => {
            if (type == "Key2Name") {
              map.set(varEntity.key, varEntity.key + '_' + varEntity.name);
            } else if (type == "Name2Key") {
              map.set(varEntity.key + '_' + varEntity.name, varEntity.key);
            }
          })
        } else {
          if (type == "Key2Name") {
            map.set(filterTempLists[0].key, filterTempLists[0].name);
          } else if (type == "Name2Key") {
            map.set(filterTempLists[0].name, filterTempLists[0].key);
          }
        }
        //重置tempLists
        tempLists = tempLists.filter(i => i.name != tempLists[0].name);
      }
    }
    return map;
  }

  /**
   * 表达式串解析，返回语义化文本
   * @param tokens 分词后token
   * @param allFunctions 函数
   * @param variableMap 变量映射map
   * @param functionSuffixs 特殊函数后缀
   * @returns 语义化文本
   */
  public static toSemantic(tokens: any, allFunctions: any[], variableMap: Map<string, string>, functionSuffixs: string[]): string {
    let result = "";

    //表达式引擎内置Math函数
    let innerMath = [];
    for (let func of allFunctions) {
      if (func.keyFront == "Math") {
        innerMath.push(func.key);
      }
    }

    for (let index = 0; index < tokens.length; index++) {
      //如果为非内置的Math函数，则保留"Math."
      if (tokens[index] == "Math" && tokens[index + 1] == "." && !innerMath.includes(tokens[index + 2])) {
        result = result + tokens[index] + tokens[index + 1] + tokens[index + 2];
        index = index + 2;
        continue;
      }

      if (functionSuffixs.includes(tokens[index]) && (tokens[index + 1] == ".")) {
        index = index + 1;
        continue;
      } else {
        var num = 0;
        //遍历会话变量和上下文变量
        if (num == 0) {
          if (tokens[index].length > 2 && tokens[index][0] == "\"" && tokens[index][tokens[index].length - 1] == "\"") {
            //校验第0位和第length-1位是否是"
            let temp = tokens[index].substring(1, tokens[index].length - 1);
            //此处需要GetContextParameter、GetSessionValue
            if (variableMap.has(temp) && tokens[index - 1] == "(" && tokens[index + 1] == ")" && this.variableFuncKeys.includes(tokens[index - 2])) {
              result = result + "\"" + variableMap.get(temp) + "\"";
              num = 1;
            }
          }
        }
        //计数器num：如果遍历allFunctions没找到对应字段，则直接拼接(num=0)；否则替换(num=1)
        if (num == 0) {
          for (let i = 0; i < allFunctions.length; i++) {
            if (tokens[index] == allFunctions[i].key) {
              if (allFunctions[i].type != "Advanced") {
                result = result + allFunctions[i].name;
                num = 1;
                break;
              }
            }
          }
        }
        //如果计数器为0，证明没有语义化匹配。显示原始数据
        if (num == 0) {
          result = result + tokens[index];
        }
      }
    }
    return result;
  }

  //格式化扩展函数返回值
  public static formatExtend(data: any): FunctionEntity[] {
    let result: FunctionEntity[] = [];
    //rpc扩展函数
    if (data && data.rpcFunctions && data.rpcFunctions.length > 0) {
      for (let f of data.rpcFunctions) {
        if (f.display) {
          //扩展函数实体
          let fun: ExtendFunction = {
            token: f.token,
            key: f.token,
            name: f.name != null ? f.name : f.token,
            type: FuncType.Extend,
            description: f.description,
            params: [],
            keyFront: null,
            className: null,
            isLatestExtendFunc: true
          };
          //有参数则遍历参数
          if (f.params && f.params.length > 0) {
            for (let p of f.params) {
              let param: Parameter = {
                key: p.paramName,
                name: (p.paramDesc != null && p.paramDesc.length > 0) ? p.paramDesc : p.paramName,
                type: "obj"
              }
              fun.params.push(param);
            }
          }
          result.push(fun);
        }
      }
    }

    //原始扩展函数（扩展在应用底座中，通过反射调用）
    if (data && data.originFunctions && data.originFunctions.length > 0) {
      for (let f of data.originFunctions) {
        //扩展函数实体
        let fun: ExtendFunction = {
          token: f.name,
          key: f.name,
          name: f.semanticName,
          type: FuncType.Extend,
          description: f.description,
          params: [],
          keyFront: f.instanceName,
          className: f.className,
          isLatestExtendFunc: false
        };
        //有参数则遍历参数
        if (f.funParameterList && f.funParameterList.length > 0) {
          for (let p of f.funParameterList) {
            let param: Parameter = {
              key: p.name,
              name: p.description,
              type: p.type
            }
            fun.params.push(param);
          }
        }
        result.push(fun);
      }
    }
    return result;
  }

  //格式化字符串
  public static format(value: string, argument: any[]) {
    // 数据长度为空，则直接返回
    if (argument.length == 0) {
      return value;
    }

    // 使用正则表达式，循环替换占位符数据
    for (let i = 0; i < argument.length; i++) {
      value = value.replace(new RegExp("\\{" + i + "\\}", "g"), argument[i]);
    }

    return value;
  }

  //获取拼接基路径的URL
  public static convertPath(url: string) {
    return BasePathService.convertPath(url);
  }

  //加载esprima
  public static loadEsprima() {
    if ((window as any).esprima == null) {
      var body = document.getElementsByTagName("body")[0];
      var esprimaScript = document.createElement("script");
      esprimaScript.type = this.ScriptType;
      esprimaScript.src = this.EsprimaScriptSrc + Date.now();
      body.appendChild(esprimaScript);
      return esprimaScript;
    }
  }

  private static ScriptType = "text/javascript";
  private static EsprimaScriptSrc = CommonUtils.convertPath("/platform/common/web/@gsp-svc/esprima/dist/esprima.js?");

}

